import { withServerProps } from '@web/library/with-server-props';
import { useTranslation } from 'react-i18next';
import { Page } from '@web/components/page';
import {
  IndexBreedersMapScreen,
  IndexBreedersMapScreenProps,
} from 'shared/screens/breeders/index-breeders-map';
import { useRoute } from 'shared/library/routes/route';
import { View } from 'react-native';
import { H1, H2 } from 'shared/components/title';
import { useMemo } from 'react';
import { cities } from 'shared/library/cities';
import { Text } from 'shared/components/text';
import { Link } from 'shared/components/link';
import { getCountryName } from 'shared/library/i18n/country-name';

export default function Breeders(props: IndexBreedersMapScreenProps) {
  const { t } = useTranslation('screens');
  const route = useRoute();

  const topCitiesByCountry = useMemo(() => {
    const grouped = cities.reduce<Record<string, typeof cities>>((acc, city) => {
      if (!acc[city.country]) {
        acc[city.country] = [];
      }
      acc[city.country]!.push(city);
      return acc;
    }, {});

    return Object.fromEntries(
      Object.entries(grouped).map(([country, countryCities]) => [
        country,
        countryCities.sort((a, b) => b.population - a.population).slice(0, 20),
      ]),
    );
  }, []);

  return (
    <Page
      {...props}
      register={(seo) =>
        seo
          .setTitle(t('breeders.index_breeders_map.title'))
          .setDescription(t('breeders.index_breeders_map.description'))
      }
    >
      <IndexBreedersMapScreen {...props} />

      <View className={'bg-white p-3 md:p-6 md:py-12'}>
        <View className={'mx-auto w-full max-w-screen-xl'}>
          <H1
            title={t('breeders.index_breeders_map.title')}
            subtitle={t('breeders.index_breeders_map.description')}
          />

          <H2
            title={t('breeders.index_breeders_map.find_breeders_in_your_city')}
            subtitle={t('breeders.index_breeders_map.find_breeders_in_your_city_description')}
          />

          {Object.entries(topCitiesByCountry).map(([countryCode, topCities]) => (
            <View key={countryCode} className={'mb-6'}>
              <Text className={'text-brand-brown-dark mb-3 text-lg font-semibold'}>
                {getCountryName(t, countryCode)}
              </Text>
              <View className={'flex-cols-2 md:flex-cols-5 flex-row flex-wrap gap-3'}>
                {topCities.map((city) => (
                  <Link
                    key={city.slug}
                    href={route('breeders.by-city', { city: city.slug })}
                    asBlock={true}
                    containerClassName={
                      'border-brand-brown-dark/15 hover:border-brand-brown-dark/30 rounded-lg border bg-white px-4 py-2'
                    }
                  >
                    {city.name}
                  </Link>
                ))}
              </View>
            </View>
          ))}
        </View>
      </View>
    </Page>
  );
}

export const getServerSideProps = withServerProps(IndexBreedersMapScreen.pageBuilder);
